import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Button,
	Form,
	Loader,
	Message,
	Modal
} from 'semantic-ui-react';
import { colors } from '../../services/constants';
import {
	mapLookupTypes,
	log
} from '../../services/utils';

import useForm from '../../hooks/useForm';

import {
	updateTag,
	createTag
} from '../../api/nora/labels';
import { getTagAuthScopes } from '../../api/nora/lookups';

import {
	useUIState,
	useUIAction
} from '../../contexts/UIContext';

import ColorPicker from '../Form/ColorPicker';
import FormInput from '../Form/FormInput';
import FormSelect from '../Form/FormSelect';
import FormErrors from '../Shared/FormErrors';
import HelpModal from '../Shared/HelpModal';

const TagModal = () => {
	const {
		tag,
		onTagModalClose
	} = useUIState();
	const {
		setTag,
		setApiErrorAlert,
		setOnTagModalClose
	} = useUIAction();

	const [ forceValidation, setForceValidation ] = useState( false );
	const [ authScopes, setAuthScopes ] = useState( [] );
	const [ loading, setLoading ] = useState( false );

	const {
		formData,
		updateField,
		reinitializeFormData,
		setFormData
	} = useForm( {} );

	useEffect( () => {
		( async () => {
			try {
				const [ { types } ] = await getTagAuthScopes();

				const mappedTypes = mapLookupTypes( types );

				setAuthScopes( mappedTypes );
			} catch ( error ) {
			// handle
			}
		} )();
	}, [] );

	useEffect( () => {
		if ( tag ) {
			setFormData( {
				color: colors[ Math.floor( Math.random() * colors.length ) ],
				...tag
			} );
		}
	}, [ tag, setFormData ] );

	const errors = useMemo( () => ( {
		scopeID: [
			{
				error: !formData.scopeID,
				message: 'Scope is a required field.',
				fieldLabel: 'Scope'
			}
		],
		text: [
			{
				error: !formData.text,
				message: 'Text is a required field.',
				fieldLabel: 'Text'
			}
		]
	} ), [
		formData.scopeID,
		formData.text
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleCloseModal = useCallback( () => {
		setTag( null );
		setOnTagModalClose( null );
	}, [
		setTag,
		setOnTagModalClose
	] );

	const handleSave = useCallback( async () => {
		setLoading( true );

		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			try {
				const res = tag?.id === 0
					? await createTag( formData )
					: await updateTag( formData );

				reinitializeFormData();
				await onTagModalClose( res );
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setLoading( false );
			handleCloseModal();
		}
	}, [
		validate,
		forceValidation,
		onTagModalClose,
		formData,
		handleCloseModal,
		tag,
		reinitializeFormData,
		setApiErrorAlert
	] );

	return (
		<Modal open={ tag && Object.values( tag ).length > 0 }>
			{ log( 'tagsModal' ) }
			<Modal.Header>
				{ tag?.id === 0
					? 'Add Tag'
					: 'Edit Tag'
				}
				<HelpModal id={ 6 } />
			</Modal.Header>
			<Modal.Content>
				<div className="tag-form-container">
					<Form
						autoComplete="off"
						error={ forceValidation && validate() }
					>
						<Form.Group>
							<FormInput
								errors={ errors.text }
								fieldLabel="Text"
								forceValidation={ forceValidation }
								maxLength={ 125 }
								name="text"
								onChange={ updateField }
								required
								value={ formData.text || '' }
								width={ 10 }
							/>
							<Form.Field
								style={ {
									display: 'flex',
									flexDirection: 'column'
								} }
								width={ 1 }
							>
								<label>Color</label>
								<ColorPicker
									colors={ colors }
									name="color"
									onChange={ updateField }
									value={ formData.color || '' }
								/>
							</Form.Field>
							<FormSelect
								errors={ errors.scopeID }
								fieldLabel="Scope"
								forceValidation={ forceValidation }
								helpId={ 38 }
								name="scopeID"
								onChange={ updateField }
								options={ authScopes }
								required
								search
								value={ formData.scopeID || '' }
								width={ 5 }
							/>
						</Form.Group>
						<FormErrors mapFormErrors={ mapFormErrors } />
					</Form>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button
					onClick={ handleSave }
				>
					{ loading
						? <>
							<Loader
								active
								inline
								size="mini"
								style={ {
									width: '12px',
									height: '12px'
								} }
							/>
								&nbsp;&nbsp;
								Saving...
						</>
						: tag?.id === 0
							? 'Save and Add'
							: 'Save and Close'
					}
				</Button>
				<Button
					content="Cancel"
					disabled={ loading }
					onClick={ handleCloseModal }
				/>
			</Modal.Actions>
		</Modal>
	);
};

export default memo( TagModal );
